import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from '@/services/auth';
import { normalizeApiError } from '@/lib/errors';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isAuthEndpoint = (url?: string) =>
  typeof url === 'string' &&
  (url.includes('/auth/signin') ||
    url.includes('/auth/signup') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/verify-token') ||
    url.includes('/auth/resend-invitation') ||
    url.includes('/users/forgot-password') ||
    url.includes('/users/confirm-reset-password') ||
    url.includes('/auth/partner-signup'));

api.interceptors.request.use((config) => {
  if (isAuthEndpoint(config.url)) {
    return config;
  }
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
},
  (error) => Promise.reject(error)
);

let isRefreshing = false;
type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };
type FailedRequest = {
  resolve: (_value: any) => void;
  reject: (_error: any) => void;
  config: RetryConfig;
};
const failedRequestsQueue: FailedRequest[] = [];

const applyAuthorizationHeader = (config: RetryConfig, token: string) => {
  const headers = config.headers ?? {};
  if (typeof (headers as any).set === 'function') {
    (headers as any).set('Authorization', `Bearer ${token}`);
    config.headers = headers;
    return;
  }
  (headers as any).Authorization = `Bearer ${token}`;
  config.headers = headers;
};

api.interceptors.response.use((response) => response, async (error: AxiosError) => {
  const status = error.response?.status;
  const originalRequest = error.config as RetryConfig | undefined;
  if (originalRequest?.url?.includes('/auth/refresh')) {
    authService.logout();
    const normalizedError = normalizeApiError(error);
    return Promise.reject(normalizedError);
  }
  if (status === 401 && originalRequest && !originalRequest._retry) {
    if (isAuthEndpoint(originalRequest.url)) {
      const normalizedError = normalizeApiError(error);
      return Promise.reject(normalizedError);
    }
    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      authService.logout();
      const normalizedError = normalizeApiError(error);
      return Promise.reject(normalizedError);
    }
    originalRequest._retry = true;
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject, config: originalRequest });
      });
    }
    isRefreshing = true;
    try {
      const { access_token } = await authService.refreshToken();
      failedRequestsQueue.forEach(({ resolve, config }) => {
        applyAuthorizationHeader(config, access_token);
        resolve(api(config));
      });
      failedRequestsQueue.length = 0;
      applyAuthorizationHeader(originalRequest, access_token);
      return api(originalRequest);
    } catch (refreshError) {
      failedRequestsQueue.forEach(({ reject }) => reject(refreshError));
      failedRequestsQueue.length = 0;
      authService.logout();
      const normalizedError = normalizeApiError(refreshError);
      return Promise.reject(normalizedError);
    } finally {
      isRefreshing = false;
    }
  }
  const normalizedError = normalizeApiError(error);
  return Promise.reject(normalizedError);
});

export default api;